'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import * as React from 'react';

import { Row, Stack } from '@/components/layout';
import {
    Breadcrumb,
    BreadcrumbEllipsis,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbMapping,
    BreadcrumbPage,
    BreadcrumbSeparator,
    useBreadcrumb,
} from '@/components/ui/breadcrumb';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useMediaQueryBelow } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

import ThemeToggle from './theme-toggle';

type HeaderProps = {
    className?: string;
};

type BreadcrumbItemData = {
    label: string;
    href: string;
};

const breadcrumbLabelClassName =
    'inline-block max-w-40 desktop:max-w-[100%] truncate align-bottom sm:max-w-56';

function generateBreadcrumbs(
    pathname: string,
    idNameMappings: BreadcrumbMapping[],
): BreadcrumbItemData[] {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItemData[] = [];

    // Always start with Home
    breadcrumbs.push({ label: 'Home', href: '/' });

    // Build breadcrumbs from path segments
    let currentPath = '';
    for (const segment of segments) {
        currentPath += `/${segment}`;

        // Check if segment is an ID that has a name mapping
        const mapping = idNameMappings.find(
            (mapping) => mapping.id.toLowerCase() === segment.toLowerCase(),
        );
        const mappedName = mapping?.name;
        const skipParentSegment = mapping?.skipParentSegment;

        // If mapping exists, use it; otherwise format the segment
        const label = mappedName
            ? mappedName
            : segment
                  .replace(/[-_]/g, ' ')
                  .replace(/\b\w/g, (char) => char.toUpperCase());

        // Remove parent segment if mapping specifies so
        if (skipParentSegment && breadcrumbs.length > 1) {
            breadcrumbs.pop();
        }

        breadcrumbs.push({ label, href: currentPath });
    }

    return breadcrumbs;
}

export function Header({ className }: HeaderProps) {
    const pathname = usePathname();
    const { idNameMappings } = useBreadcrumb();
    const breadcrumbs = generateBreadcrumbs(pathname, idNameMappings);
    const isMobile = useMediaQueryBelow('desktop');

    // On mobile, show only first, ellipsis, and last if there are more than 2 items
    const shouldShowEllipsis = isMobile && breadcrumbs.length > 2;

    // Get hidden breadcrumbs (all items between first and last)
    const hiddenBreadcrumbs = shouldShowEllipsis
        ? breadcrumbs.slice(1, breadcrumbs.length - 1)
        : [];

    return (
        <Row
            className={cn(
                'box-border h-12 items-center gap-0',
                'border-border border-b',
                'w-full',
                className,
            )}
        >
            {/* Sidebar Toggle */}
            <Row className="h-full items-center">
                <Row className="h-full items-center justify-center">
                    <SidebarTrigger className="size-12" />
                </Row>
            </Row>

            {/* Vertical Separator */}
            <Row className="h-full items-center">
                <Separator orientation="vertical" className="h-5" />
            </Row>

            {/* Header Content */}
            <Row className="grow items-center gap-4 px-4">
                {/* Breadcrumb */}
                <Row className="grow items-center overflow-hidden px-2.5">
                    <Breadcrumb className="min-w-0">
                        <BreadcrumbList>
                            {shouldShowEllipsis ? (
                                <>
                                    {/* First breadcrumb */}
                                    <BreadcrumbItem>
                                        <BreadcrumbLink asChild>
                                            <Link
                                                href={breadcrumbs[0].href}
                                                className="min-w-0 font-sans leading-7 font-semibold"
                                            >
                                                <span
                                                    className={
                                                        breadcrumbLabelClassName
                                                    }
                                                >
                                                    {breadcrumbs[0].label}
                                                </span>
                                            </Link>
                                        </BreadcrumbLink>
                                    </BreadcrumbItem>
                                    <BreadcrumbSeparator />
                                    {/* Ellipsis with dropdown */}
                                    <BreadcrumbItem>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger className="flex items-center gap-1">
                                                <BreadcrumbEllipsis className="size-4" />
                                                <span className="sr-only">
                                                    Toggle menu
                                                </span>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="start">
                                                {hiddenBreadcrumbs.map(
                                                    (item, index) => (
                                                        <Stack key={item.href}>
                                                            <DropdownMenuItem
                                                                asChild
                                                            >
                                                                <Link
                                                                    href={
                                                                        item.href
                                                                    }
                                                                >
                                                                    {item.label}
                                                                </Link>
                                                            </DropdownMenuItem>
                                                            {index !==
                                                                hiddenBreadcrumbs.length -
                                                                    1 && (
                                                                <DropdownMenuSeparator />
                                                            )}
                                                        </Stack>
                                                    ),
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </BreadcrumbItem>
                                    <BreadcrumbSeparator />
                                    {/* Last breadcrumb */}
                                    <BreadcrumbItem>
                                        <BreadcrumbPage className="min-w-0 font-sans leading-7 font-semibold">
                                            <span
                                                className={
                                                    breadcrumbLabelClassName
                                                }
                                            >
                                                {
                                                    breadcrumbs[
                                                        breadcrumbs.length - 1
                                                    ].label
                                                }
                                            </span>
                                        </BreadcrumbPage>
                                    </BreadcrumbItem>
                                </>
                            ) : (
                                <>
                                    {/* Show all breadcrumbs */}
                                    {breadcrumbs.map((item, index) => {
                                        const isLast =
                                            index === breadcrumbs.length - 1;
                                        return (
                                            <React.Fragment key={item.href}>
                                                <BreadcrumbItem>
                                                    {isLast ? (
                                                        <BreadcrumbPage className="min-w-0 font-sans leading-7 font-semibold">
                                                            <span
                                                                className={
                                                                    breadcrumbLabelClassName
                                                                }
                                                            >
                                                                {item.label}
                                                            </span>
                                                        </BreadcrumbPage>
                                                    ) : (
                                                        <BreadcrumbLink asChild>
                                                            <Link
                                                                href={item.href}
                                                                className="min-w-0 font-sans leading-7 font-semibold"
                                                            >
                                                                <span
                                                                    className={
                                                                        breadcrumbLabelClassName
                                                                    }
                                                                >
                                                                    {item.label}
                                                                </span>
                                                            </Link>
                                                        </BreadcrumbLink>
                                                    )}
                                                </BreadcrumbItem>
                                                {!isLast && (
                                                    <BreadcrumbSeparator />
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </>
                            )}
                        </BreadcrumbList>
                    </Breadcrumb>
                </Row>

                {!isMobile && <ThemeToggle />}
            </Row>
        </Row>
    );
}
